/**
 * The template gallery — `+ Add ▸ Board ▸ Start with template`, and the `Templates` tab
 * `CreateBoardDialog` reserved in M1-B1 §3.3.
 *
 * monday reaches templates from the Add menu's submenu (`New Board · New multi-level board ·
 * Start with template`, measured) and from the account Template center. Ours serves both
 * entry points from this one component, so the two flows cannot drift.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { useOverlayDismiss, useToast } from '@elizmonday/ui';
import { useTRPC, useTRPCClient } from '../../lib/trpc';
import { boardUrl } from '../shell/urls';
import './templates.css';

export function TemplatePicker({
  workspaceId,
  folderId,
  onClose,
}: {
  workspaceId: string | null;
  folderId?: string | null;
  onClose(): void;
}) {
  const trpc = useTRPC();
  const client = useTRPCClient();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();

  /* P-013: Escape closes the gallery too, like `SaveAsTemplateDialog` beside it (P-224). */
  useOverlayDismiss({ onClose });

  const [selected, setSelected] = useState<string | null>(null);
  const [name, setName] = useState('');

  const templates = useQuery({ ...trpc.templates.list.queryOptions({}), staleTime: 30_000 });

  const create = useMutation({
    mutationFn: () =>
      client.templates.createBoard.mutate({
        templateId: selected!,
        ...(name.trim() === '' ? {} : { name: name.trim() }),
        ...(workspaceId ? { workspaceId } : {}),
        ...(folderId ? { folderId } : {}),
      }),
    onSuccess: async (board) => {
      await queryClient.invalidateQueries();
      toast(`${board.name} created`, { kind: 'positive' });
      onClose();
      navigate(`${boardUrl(board.id)}/views/${board.viewId}`);
    },
    onError: (error: unknown) => {
      toast(error instanceof Error ? error.message : 'The board could not be created', {
        kind: 'negative',
      });
    },
  });

  const rows = templates.data ?? [];

  return (
    <div className="ez-dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className="ez-template-picker"
        role="dialog"
        aria-modal="true"
        aria-label="Start with template"
        data-testid="template-picker"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <h2>Start with template</h2>

        {templates.isPending ? (
          <p className="ez-template-picker__empty">Loading templates…</p>
        ) : rows.length === 0 ? (
          <p className="ez-template-picker__empty" data-testid="template-picker-empty">
            No templates yet. Save a board as a template from its <strong>···</strong> menu and it
            will appear here.
          </p>
        ) : (
          <ul className="ez-template-picker__grid" role="listbox" aria-label="Templates">
            {rows.map((template) => (
              <li key={template.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={selected === template.id}
                  className="ez-template-card"
                  data-testid={`template-card-${template.id}`}
                  data-selected={selected === template.id ? 'true' : 'false'}
                  onClick={() => {
                    setSelected(template.id);
                    if (name.trim() === '') setName(template.name.replace(/ template$/, ''));
                  }}
                >
                  <span className="ez-template-card__name">{template.name}</span>
                  <span className="ez-template-card__meta">
                    {template.columnCount} columns · {template.groupCount} groups ·{' '}
                    {template.itemCount} items
                  </span>
                  {template.kind === 'private' ? (
                    <span className="ez-template-card__badge">Private</span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        )}

        {selected === null ? null : (
          <label className="ez-template-picker__field">
            Board name
            <input
              data-testid="template-picker-name"
              value={name}
              maxLength={255}
              onChange={(event) => setName(event.target.value)}
            />
          </label>
        )}

        <footer className="ez-template-picker__foot">
          <button type="button" className="ez-template-dialog__cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="ez-template-dialog__save"
            data-testid="template-picker-create"
            disabled={selected === null || create.isPending}
            onClick={() => create.mutate()}
          >
            Use template
          </button>
        </footer>
      </div>
    </div>
  );
}
